/* eslint-disable no-undef */
/* =============================================================================
 * App shell — sidebar, top bar with manager switcher, routing
 * ===========================================================================*/

import React, { useState, useEffect, useRef } from "react";
import { Icon, ToastHost, useToast, mgrColor, initials } from "./components.jsx";
import { Dashboard } from "./screens-dashboard.jsx";
import { Properties } from "./screens-properties.jsx";
import { Invoices } from "./screens-invoices.jsx";
import { Templates } from "./screens-templates.jsx";
import { Mortgages } from "./screens-mortgages.jsx";
import { Sensors, Connections } from "./screens-sensors.jsx";
import { Analytics } from "./screens-analytics.jsx";
import { fetchManagers, fetchProperties, fetchSensors, mapSensor, fetchBillPreview, fetchInvoices, mapInvoice, fetchMe, fetchQboStatus } from "./api.js";
import { isAuthEnabled, authSignOut } from "./auth.jsx";

const NAV = [
  { key: "dashboard", label: "Overview", icon: "dashboard" },
  { section: "Portfolio" },
  { key: "properties", label: "Properties & Units", icon: "building" },
  { key: "usage", label: "Usage", icon: "chart" },
  { section: "Billing" },
  { key: "invoices", label: "Invoices", icon: "receipt" },
  { key: "templates", label: "Rate Templates", icon: "template" },
  { key: "mortgages", label: "Mortgages", icon: "landmark" },
  // Not a browse tree — the exception list. A unit-first hierarchy can tell you everything about a unit except
  // "which sensor is dying?", because you'd have to open every unit to find out. That's what this answers.
  { section: "Attention" },
  { key: "sensors", label: "Sensor health", icon: "sensor" },
  { section: "Setup" },
  { key: "connections", label: "QuickBooks", icon: "link" },
];

const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

function ManagerSwitcher({ mgr, setMgr, managers, unitCounts, canSwitch = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const curName = mgr === "all" ? "All Managers" : ((managers.find((m) => m.id === mgr) || {}).name || "Manager");
  const curSub = mgr === "all" ? `${managers.length} account${managers.length === 1 ? "" : "s"}` : `${unitCounts[mgr] || 0} units`;
  // A single-manager (non-admin) user is pinned to their own account — render a static chip, no dropdown.
  if (!canSwitch) {
    return (
      <div className="switcher">
        <div className="switcher__btn" style={{ cursor: "default" }}>
          <span className="av" style={{ background: mgrColor(mgr) }}>{mgr === "all" ? "ALL" : initials(curName)}</span>
          <span className="lbl"><b>{curName}</b><span>{curSub}</span></span>
        </div>
      </div>
    );
  }
  return (
    <div className="switcher" ref={ref}>
      <button className="switcher__btn" onClick={() => setOpen((o) => !o)}>
        <span className="av" style={{ background: mgrColor(mgr) }}>{mgr === "all" ? "ALL" : initials(curName)}</span>
        <span className="lbl"><b>{curName}</b><span>{curSub}</span></span>
        <Icon name="chevronDown" size={16} className="caret" />
      </button>
      {open && (
        <div className="switcher__menu">
          <div className={`switcher__opt ${mgr === "all" ? "is-active" : ""}`} onClick={() => { setMgr("all"); setOpen(false); }}>
            <span className="av" style={{ background: mgrColor("all") }}>ALL</span>
            <span className="lbl"><b>All Managers</b><span>Portfolio-wide view</span></span>
            {mgr === "all" && <Icon name="check" size={16} className="chk" />}
          </div>
          <div className="switcher__divider" />
          {managers.map((m) => (
            <div key={m.id} className={`switcher__opt ${mgr === m.id ? "is-active" : ""}`} onClick={() => { setMgr(m.id); setOpen(false); }}>
              <span className="av" style={{ background: mgrColor(m.id) }}>{initials(m.name)}</span>
              <span className="lbl"><b>{m.name}</b><span>{unitCounts[m.id] || 0} units{m.active ? "" : " · inactive"}</span></span>
              {mgr === m.id && <Icon name="check" size={16} className="chk" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const BADGE_TITLES = { dashboard: "need attention", sensors: "assigned meters need attention", invoices: "units ready to bill" };
function Sidebar({ route, go, mgr, badges, managers, me }) {
  const m = mgr === "all" ? null : (managers || []).find((x) => x.id === mgr);
  // Real push mode for the badge — this used to be a hardcoded "dry run" label that contradicted the
  // Mortgages header once sandbox push went live. Same source of truth as that screen: /api/qbo/status.
  const [qboStat, setQboStat] = useState(null);
  useEffect(() => { fetchQboStatus().then(setQboStat).catch(() => setQboStat(null)); }, []);
  const qboLive = !!(qboStat && qboStat.pushEnabled && qboStat.configured);
  return (
    <aside className="sidebar">
      <div className="sidebar__logo">
        <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--accent)", display: "grid", placeItems: "center", flex: "0 0 34px" }}>
          <Icon name="droplet" size={20} style={{ color: "#fff" }} />
        </div>
        <div className="wm">MSync<small>Water Metering</small></div>
      </div>
      <nav className="sidebar__nav">
        {NAV.map((n, i) => (
          n.section
            ? <div key={"s" + i} className="sidebar__section" style={{ marginTop: i ? 14 : 0 }}>{n.section}</div>
            : <a key={n.key} className={`nav-item ${route === n.key ? "is-active" : ""}`} onClick={() => go(n.key)}>
                <Icon name={n.icon} size={19} />
                {n.label}
                {badges[n.key] > 0 && <span className="nav-item__badge" title={BADGE_TITLES[n.key] ? `${badges[n.key]} ${BADGE_TITLES[n.key]}` : undefined}>{badges[n.key]}</span>}
              </a>
        ))}
      </nav>
      <div className="sidebar__spacer" />
      <div className="sidebar__foot">
        <div className="qbo-mini">
          <div className="qbo-mini__row">
            <span className="qbo-mini__dot" style={{ background: qboLive ? "var(--color-success)" : "var(--color-warning-text)" }} />
            QuickBooks · {qboLive ? `live · ${qboStat.environment}` : "dry run"}
          </div>
          <div className="qbo-mini__sub">{m ? m.name : "Monnit auto-sync · daily 9 AM"}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px 2px" }}>
          <span className="av" style={{ background: me?.isAdmin ? "#475569" : mgrColor(mgr) }}>{initials(me?.name || "U")}</span>
          <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.2, overflow: "hidden" }}>
            <b style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{me?.name || "Signed in"}</b>
            <span style={{ fontSize: 11, opacity: .7 }}>{me ? (me.isAdmin ? "Administrator" : "Property manager") : "…"}</span>
          </span>
          {isAuthEnabled() && (
            <button className="iconbtn" title="Sign out" onClick={authSignOut}><Icon name="logout" size={18} /></button>
          )}
        </div>
      </div>
    </aside>
  );
}

function Placeholder({ title }) {
  return <div className="page"><div className="page__head"><div><h1>{title}</h1><p>Coming up next…</p></div></div><div className="card"><div className="empty">This screen is being built.</div></div></div>;
}

// Single accent: Arctic Sky. The 4-way accent picker was retired 2026-08-08 (design-phase leftover);
// theme.css still carries the other accent classes if it ever comes back.

// URL-hash routing: each screen has a #route so a refresh restores it (open drawers/params reset, which is fine).
const ROUTE_KEYS = NAV.filter((n) => n.key).map((n) => n.key);
const routeFromHash = () => { const h = window.location.hash.replace(/^#\/?/, ""); return ROUTE_KEYS.includes(h) ? h : "dashboard"; };

// The QBO OAuth callback bounces back with ?qbo=connected|error&realm=... — before this, the round-trip ended in
// silence on the Overview. Must render INSIDE ToastHost: useToast at the App level gets the context's no-op default.
function QboReturnNotice() {
  const toast = useToast();
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const st = qs.get("qbo");
    if (!st) return;
    const realm = qs.get("realm");
    toast(st === "connected"
      ? { title: "QuickBooks company connected", body: `Realm ${realm || "?"} is now linked.`, duration: 9000 }
      : st === "reconnected"
        ? { title: "Same company re-linked — not a new connection", body: `Realm ${realm || "?"} was already connected; Intuit auto-picked it from your active session without showing a login. To link a DIFFERENT company: sign out of Intuit or use a private window — and note sandbox (Development) keys can only ever connect sandbox companies. A live company needs the app's Production keys.`, duration: 12000 }
        : { title: "QuickBooks connect failed", body: "Intuit returned an error — the company was not connected.", err: true, duration: 9000 });
    window.history.replaceState(null, "", window.location.pathname + window.location.hash);   // don't re-toast on refresh
  }, []);
  return null;
}

function App() {
  const [mgr, setMgr] = useState(() => localStorage.getItem("msync-mgr") || "all");   // survives refresh, like the route (hash) and theme
  const [route, setRoute] = useState(routeFromHash);
  const [params, setParams] = useState({});
  const [navOpen, setNavOpen] = useState(false);
  // Light/dark follows the OS preference until the user explicitly toggles — only the toggle persists.
  const [mode, setMode] = useState(() => localStorage.getItem("msync-theme-mode")
    || (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"));

  // live data for the switcher + sidebar badges
  const [managers, setManagers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [eligible, setEligible] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [me, setMe] = useState(null);   // /api/me — identity + scope (admin vs a single manager)

  useEffect(() => { document.body.className = `theme-sky mode-${mode}`; }, [mode]);

  // Keep `route` in sync with the URL hash so refresh restores the screen and back/forward work.
  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    fetchMe().then(setMe).catch(() => setMe(null));
    fetchManagers().then((m) => setManagers(m || [])).catch(() => setManagers([]));
    fetchProperties().then((p) => setProperties(p || [])).catch(() => setProperties([]));
  }, []);

  // Sensor list feeds the sidebar badges — scope it to the selected manager like the other badge
  // counts, or the badge shows the whole portfolio's attention items next to a scoped screen.
  useEffect(() => {
    let ok = true;
    fetchSensors(mgr === "all" ? null : mgr).then((rows) => {
      if (!ok) return;
      const newest = Math.max(0, ...rows.map((r) => (r.lastComm ? new Date(r.lastComm).getTime() : 0)));
      setSensors((rows || []).map((r) => mapSensor(r, newest)));
    }).catch(() => { if (ok) setSensors([]); });
    return () => { ok = false; };
  }, [mgr]);

  // Pin a single-manager (non-admin) user to their own account; admins keep the All-Managers switcher.
  useEffect(() => {
    if (me && !me.isAdmin && managers.length === 1) setMgr(managers[0].id);
  }, [me, managers]);

  // Remember the selected scope across refreshes, but never restore one this user can't see (access revoked, or a
  // different sign-in on the same browser) — fall back to All. Guarded on managers.length so the empty list before
  // the fetch resolves reads as "not loaded yet", not "no access".
  useEffect(() => { localStorage.setItem("msync-mgr", mgr); }, [mgr]);
  useEffect(() => {
    if (mgr !== "all" && managers.length && !managers.some((m) => String(m.id) === String(mgr))) setMgr("all");
  }, [managers, mgr]);

  // eligible-to-bill + overdue counts, scoped to the selected manager
  useEffect(() => {
    let ok = true;
    const m = mgr === "all" ? null : mgr;
    fetchBillPreview(m, isoToday()).then((rows) => { if (ok) setEligible((rows || []).filter((r) => r.eligible).length); }).catch(() => { if (ok) setEligible(0); });
    fetchInvoices(m).then((rows) => { if (ok) setOverdue((rows || []).map(mapInvoice).filter((i) => i.status === "Overdue").length); }).catch(() => { if (ok) setOverdue(0); });
    return () => { ok = false; };
  }, [mgr]);

  const go = (r, p = {}) => { setRoute(r); setParams(p); setNavOpen(false); window.scrollTo(0, 0); if (routeFromHash() !== r) window.location.hash = r; };
  // The live unit drawer lives on the Properties screen; deep-link there so "Open unit" works from
  // the sensor drawer, invoice run, etc.
  const openUnit = (id) => go("properties", { unitId: id });

  // per-manager unit counts for the switcher
  const unitCounts = {};
  properties.forEach((p) => { unitCounts[p.managerId] = (unitCounts[p.managerId] || 0) + p.unitCount; });

  // Admins (and managers spanning >1 account) get the switcher; a lone-account manager is pinned.
  const canSwitch = me ? (me.isAdmin || managers.length > 1) : true;

  // badges (live): sensor issues, eligible-to-bill, and attention = issues + overdue invoices
  const sensorIssues = sensors.filter((s) => s.alertsActive).length;
  const attention = sensorIssues + overdue;
  const badges = { dashboard: attention, invoices: eligible, sensors: sensorIssues };

  const screens = {
    dashboard: Dashboard, properties: Properties,
    invoices: Invoices, templates: Templates,
    mortgages: Mortgages,
    sensors: Sensors, connections: Connections,
    usage: Analytics,
  };
  const Screen = screens[route];
  const titleMap = Object.fromEntries(NAV.filter((n) => n.key).map((n) => [n.key, n.label]));

  const props = { mgr, go, openUnit, params, setMgr, managers };

  return (
    <ToastHost>
      <QboReturnNotice />
      <div className={`app ${navOpen ? "nav-open" : ""}`}>
        <div className="nav-scrim" onClick={() => setNavOpen(false)} />
        <Sidebar route={route} go={go} mgr={mgr} badges={badges} managers={managers} me={me} />
        <div className="main">
          <div className="topbar">
            <button className="nav-toggle" onClick={() => setNavOpen((o) => !o)} aria-label="Menu"><Icon name="dashboard" size={18} /></button>
            <div className="topbar__title">{titleMap[route]}<small>MSync · Monnit → QuickBooks billing</small></div>
            <div className="topbar__spacer" />
            <button className="iconbtn" title={mode === "dark" ? "Switch to light" : "Switch to dark"}
              onClick={() => { const next = mode === "dark" ? "light" : "dark"; setMode(next); localStorage.setItem("msync-theme-mode", next); }}>
              <Icon name={mode === "dark" ? "sun" : "moon"} size={18} />
            </button>
            <button className="iconbtn" onClick={() => go("dashboard")}><Icon name="bell" size={18} />{attention > 0 && <span className="dot" />}</button>
            <ManagerSwitcher mgr={mgr} setMgr={setMgr} managers={managers} unitCounts={unitCounts} canSwitch={canSwitch} />
          </div>
          {Screen ? <Screen {...props} /> : <Placeholder title={titleMap[route]} />}
        </div>
      </div>
    </ToastHost>
  );
}

export default App;
